import OffCanvas from '../components/Shared/OffCanvas'
import useOffCanvas from '../hooks/useOffCanvas'
import useModal from '../hooks/useModal'
import Modal from '../components/Shared/Modal'
import CardsmTicket from '../components/ticket/CardsmTicket'
import TicketHelpPanel from '../components/Support/TicketHelpPanel'
import AddTicket from '../components/ticket/AddTicket'
import EmergencyInfo from '../components/Info/EmergencyInfo'
import CreateModeSelector from '../components/TicketCreate/CreateModeSelector'
import TicketWizard from '../components/TicketCreate/TicketWizard'
import useMyTickets from '../hooks/useMyTickets'
import usePersistedState from '../hooks/usePersistedState'
import { getCreateMode } from '../utils/ticketCreateFlows'
import { Link, useSearchParams } from 'react-router-dom'

/** Componente de cada modo de creación, por clave de `CREATE_MODES`. */
const MODE_COMPONENTS = {
  guiado: TicketWizard,
  formulario: AddTicket,
}

/**
 * Página /nuevo-ticket. Sin `?modo=` muestra las opciones de creación
 * (paso a paso o formulario avanzado); con él, el modo elegido. La elección
 * vive en la URL para que recargar y "atrás" funcionen. El panel lateral de
 * ayuda sólo acompaña a los modos que lo piden (`showSidebar`).
 */
const CreateTicket = () => {
  const { tickets: lastTickets } = useMyTickets({ limit: 2 })
  const [searchParams, setSearchParams] = useSearchParams()
  const [lastMode, setLastMode] = usePersistedState(
    'createTicket.lastMode',
    null
  )

  const mode = getCreateMode(searchParams.get('modo'))
  const ModeComponent = mode ? MODE_COMPONENTS[mode.key] : null
  const showSidebar = !!mode?.showSidebar

  const selectMode = (key) => {
    setLastMode(key)
    setSearchParams({ modo: key })
  }

  const { isOpen, config, close } = useOffCanvas()
  const {
    isOpen: isModalOpen,
    modalConfig,
    closeModal,
    isLargeModal,
  } = useModal()

  return (
    <>
      {/* OFFCANVAS */}
      {isOpen && (
        <OffCanvas isOpen={isOpen} onClose={close} title={config.title}>
          <div>{config.component}</div>
        </OffCanvas>
      )}

      {/* MODAL */}
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={modalConfig.title}
          isLarge={isLargeModal}
        >
          <div>{modalConfig.component}</div>
        </Modal>
      )}

      {/* MAIN LAYOUT */}
      <div className="container-fluid py-3 create-ticket-page">
        {/* ENCABEZADO DE PÁGINA */}
        <div className="ct-page-head">
          <div
            className="ct-page-icon"
            style={
              mode
                ? { color: mode.color, background: `${mode.color}1f` }
                : undefined
            }
          >
            <i className={`fa-solid ${mode?.icon || 'fa-ticket'}`} />
          </div>
          <div className="flex-grow-1">
            <h4 className="ct-page-title mb-0">
              Nuevo ticket{mode && <span> · {mode.title}</span>}
            </h4>
            <p className="ct-page-sub mb-0">
              Describa el problema o la solicitud; el equipo de TI recibe el
              caso apenas lo envía.
            </p>
          </div>
          {mode && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary ct-page-kb ct-change-mode"
              onClick={() => setSearchParams({})}
            >
              <i className="fa-solid fa-shuffle me-2" />
              Cambiar modo
            </button>
          )}
          <Link
            to="/soluciones"
            className="btn btn-sm btn-outline-secondary ct-page-kb"
          >
            <i className="fa-solid fa-book me-2" />
            Base de conocimiento
          </Link>
        </div>

        <div className="row g-3">
          {/* COLUMNA PRINCIPAL: sin panel lateral queda centrada. */}
          <div
            className={
              showSidebar ? 'col-12 col-lg-8 order-1' : 'col-12 ct-main-narrow'
            }
          >
            {ModeComponent ? (
              // `key` reinicia el estado al cambiar de modo.
              <ModeComponent key={mode.key} />
            ) : (
              <CreateModeSelector onSelect={selectMode} lastUsed={lastMode} />
            )}
          </div>

          {/* COLUMNA SIDEBAR (DERECHA) */}
          {showSidebar && (
            <div className="col-12 col-lg-4 order-2">
              <div className="ct-sidebar d-flex flex-column gap-3">
                {/* DESVÍO A INCIDENTES DE SEGURIDAD */}
                {/* Los incidentes del SGSI viven en InventoryIT, no en la tabla
                  de tickets: aquí sólo se orienta al usuario al formulario
                  correcto para no mezclar ambos registros. */}
                <div className="ct-card ct-card-security p-3">
                  <div className="ct-card-head">
                    <i className="fa-solid fa-shield-halved" />
                    <span>¿Es un incidente de seguridad?</span>
                  </div>
                  <p className="small text-body-secondary mb-2">
                    Los correos sospechosos, contraseñas comprometidas, equipos
                    perdidos o fugas de información{' '}
                    <strong className="text-body">no se reportan aquí</strong>:
                    se registran en la bitácora de seguridad de la información.
                  </p>
                  <Link
                    to="/reportar-incidente"
                    className="btn btn-sm btn-outline-primary w-100"
                  >
                    <i className="fa-solid fa-arrow-right me-2" />
                    Reportar incidente de seguridad
                  </Link>
                </div>

                {/* AYUDA: TIPOS DE REGISTRO E IMPACTO/URGENCIA */}
                <div className="ct-card">
                  <TicketHelpPanel />
                </div>

                {/* TICKETS RECIENTES */}
                <div className="ct-card p-3">
                  <div className="ct-card-head">
                    <i className="fa-solid fa-clock-rotate-left" />
                    <span>Mis tickets recientes</span>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    {lastTickets.length === 0 ? (
                      <div className="ct-empty">
                        <i className="fa-regular fa-folder-open" />
                        <span>No has creado ningún ticket aún.</span>
                      </div>
                    ) : (
                      lastTickets.map((ticket) => (
                        <CardsmTicket key={ticket.id} ticket={ticket} />
                      ))
                    )}
                  </div>
                </div>

                {/* INFORMACIÓN DE EMERGENCIA */}
                <div className="ct-card">
                  <EmergencyInfo />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .create-ticket-page { max-width: 1400px; }
        .ct-main-narrow { max-width: 920px; margin: 0 auto; }

        /* Encabezado de la página */
        .ct-page-head {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 18px;
        }
        .ct-page-icon {
          width: 46px;
          height: 46px;
          flex-shrink: 0;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          color: var(--gm-primary, #3b82f6);
          background: var(--gm-primary-soft, rgba(59,130,246,0.12));
        }
        .ct-page-title { font-weight: 700; color: var(--gm-text, #1e293b); }
        .ct-page-sub {
          font-size: 0.85rem;
          color: var(--gm-text-muted, #64748b);
        }
        .ct-page-kb { flex-shrink: 0; border-radius: 9px; }
        @media (max-width: 575px) {
          .ct-page-kb { display: none; }
          /* Cambiar de modo sí debe estar disponible en móvil. */
          .ct-page-kb.ct-change-mode { display: inline-block; }
        }

        .ct-card {
          background: var(--gm-surface, #fff);
          border: 1px solid var(--gm-border, #e2e8f0);
          border-radius: 14px;
          overflow: hidden;
          /* Nunca comprimir la tarjeta: al ser hija de un flex column,
             encogerla recorta su contenido. */
          flex-shrink: 0;
          transition: border-color var(--gm-transition, 0.2s ease);
        }
        .ct-card:hover {
          border-color: var(--gm-border-strong, var(--gm-border, #cbd5e1));
        }

        .ct-card-head {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--gm-text-muted, #64748b);
          margin-bottom: 14px;
        }
        .ct-card-head i { color: var(--gm-primary, #3b82f6); }

        /* El aviso de seguridad se destaca sin competir con el formulario */
        .ct-card-security {
          border-color: var(--gm-primary, #3b82f6);
          background: var(--gm-primary-soft, rgba(59, 130, 246, 0.08));
        }
        .ct-card-security .ct-card-head { margin-bottom: 10px; }

        .ct-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          padding: 22px 8px;
          text-align: center;
          font-size: 0.82rem;
          color: var(--gm-text-muted, #94a3b8);
        }
        .ct-empty i { font-size: 1.4rem; opacity: 0.5; }

        /* La barra lateral scrollea con la página: fijarla recortaba las
           tarjetas cuando su contenido supera el alto de la pantalla. */
      `}</style>
    </>
  )
}

export default CreateTicket
